import { reverse } from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { term } from 'shared/common/packages/JsonQuery';
import { SaveSearchProvider as Provider } from 'shared/common/packages/SaveSearchModal';
import { IJsonQuery } from 'shared/common/packages/api-models/common/json_query';
import { selectMapBounds } from 'shared/map-search/selectors/mapBounds';
import { selectJsonQuery } from 'shared/map-search/selectors/results';
import { getSaveSearchMicrofrontend } from 'shared/map-search/services/saveSearch';

import { subscribeOnAuthentification } from '../../actions/authentication';
import { selectQueryString } from '../../selectors/queryString';
import { selectUser } from '../../selectors/user';
import { TThunkDispatch } from '../../types/redux';
import { useApplicationContext } from '../ApplicationContext';

enum ESource {
  Card = 'card',
  Listing = 'listing',
  Map = 'map',
}

export const SaveSearchModalProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const dispatch = useDispatch<TThunkDispatch>();

  const context = useApplicationContext();
  const fetchManifest = React.useCallback(async () => getSaveSearchMicrofrontend(context), [context]);

  const [title, setTitle] = React.useState<string | null>(null);
  const user = useSelector(selectUser);
  const email = React.useMemo(() => (user?.isAuthenticated ? user.email : null), [user]);
  const isAuthenticated = !!user?.isAuthenticated;

  const queryString = useSelector(selectQueryString);
  const jsonQuery = useSelector(selectJsonQuery);
  const mapBounds = useSelector(selectMapBounds);
  const jsonQueryWithBounds: IJsonQuery = {
    ...jsonQuery,
    bbox: term(mapBounds.bounds?.map(bounds => reverse(bounds)) as [YMaps.TCoord, YMaps.TCoord]),
  };

  React.useEffect(() => {
    setTitle(document.title);
  }, []);

  const params = {
    email,
    title,
    isAuthenticated,
    source: ESource.Map,
    jsonQuery: jsonQueryWithBounds,
    analyticsParams: {
      pageType: 'Listing',
      listingType: ESource.Map,
      isFromBanner: false,
      queryString,
    },
  };

  React.useEffect(() => {
    if (!isAuthenticated) {
      dispatch(subscribeOnAuthentification());
    }
  }, [isAuthenticated]);

  return (
    <Provider fetchManifest={fetchManifest} params={params}>
      {children}
    </Provider>
  );
};
